// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * SRM Documentation Dialog
 *
 * Explains how Software Risk Management analysis works,
 * the review process, and how to configure knowledge base items.
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Button,
  ScrollArea,
} from '@/components/ui'
import {
  BookOpen,
  Shield,
  Lock,
  Accessibility,
  Workflow,
  Database,
  FileText,
  AlertTriangle,
  Upload,
  Users,
} from 'lucide-react'

export const DocsDialog: FC = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1" data-testid="srm-docs-button">
          <BookOpen className="h-4 w-4" />
          Docs
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh]" data-testid="srm-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <BookOpen className="h-5 w-5" />
            Software Risk Management
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[65vh] pr-4">
          <div className="space-y-8 text-sm">
            {/* Overview */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                Overview
              </h2>
              <p className="text-muted-foreground mb-3">
                SRM is UMD's process for reviewing software before procurement. Three teams—Privacy,
                Security, and Accessibility—review vendor documents concurrently to identify compliance
                risks before purchase.
              </p>
              <p className="text-muted-foreground">
                DocReview provides <strong>AI-assisted analysis</strong> that augments human reviewers.
                The AI identifies relevant sections and flags potential issues; human reviewers make
                final approval decisions.
              </p>
            </section>

            {/* Upload Portal */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Upload className="h-5 w-5 text-indigo-500" />
                Upload Portal
              </h2>
              <p className="text-muted-foreground mb-3">
                The public upload portal lets vendors or requesters submit documents directly. Embed
                the portal link in ServiceNow forms, Google Forms, or any intake page.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-xs">
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Access:</strong> Anyone with campus CAS login
                  can upload documents and view their own submissions.
                </p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Privacy:</strong> Users only see their own
                  uploads—not other submissions.
                </p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Tracking:</strong> Each submission gets an
                  auto-assigned ID (SRM-1, SRM-2) for Workday cross-reference.
                </p>
              </div>
            </section>

            {/* The Three Tracks */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Users className="h-5 w-5 text-green-500" />
                Review Tracks
              </h2>
              <p className="text-muted-foreground mb-3">
                Three teams review concurrently. Each track has designated documents and its own
                Knowledge Base with compliance guidance.
              </p>
              <div className="space-y-3">
                <Track
                  icon={<Lock className="h-4 w-4" />}
                  name="Privacy Review"
                  team="Privacy Team"
                  documents="Data Privacy Questionnaire, Vendor Privacy Notice"
                  checks="Data collection, FERPA/HIPAA/GDPR, third-party sharing, AI usage"
                />
                <Track
                  icon={<Shield className="h-4 w-4" />}
                  name="Security Review"
                  team="IT Compliance"
                  documents="SOC 2 Type 2 Report, Security Addendum"
                  checks="SOC 2 opinion, control exceptions, encryption, breach history"
                />
                <Track
                  icon={<Accessibility className="h-4 w-4" />}
                  name="Accessibility Review"
                  team="ADA Team"
                  documents="VPAT, IT Accessibility Evaluation Form"
                  checks="WCAG 2.1 AA conformance, keyboard access, screen reader support"
                />
              </div>
            </section>

            {/* How It Works */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Workflow className="h-5 w-5 text-purple-500" />
                How Analysis Works
              </h2>
              <div className="space-y-4">
                <Step
                  number={1}
                  title="Document Upload"
                  description="Upload vendor documents to the appropriate slots. Documents are grouped by request ID."
                />
                <Step
                  number={2}
                  title="Text Extraction"
                  description="Readable text is extracted from each document. Documents are analyzed individually."
                />
                <Step
                  number={3}
                  title="KB-Driven Analysis"
                  description="AI scans each document against Knowledge Base guidance items. Items are batched (5 at a time) to optimize for detailed analysis."
                />
                <Step
                  number={4}
                  title="Human Review"
                  description="Review teams examine AI findings, add context, and make approval decisions. Export report for Workday."
                />
              </div>
            </section>

            {/* Understanding Risk Metrics */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Understanding Findings
              </h2>
              <p className="text-muted-foreground mb-4">
                Each finding shows two risk indicators:
              </p>
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded text-xs font-mono bg-secondary text-secondary-foreground">3/5</span>
                    <span className="font-medium">KB Risk Score</span>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    <strong>Static weight</strong> assigned to each KB item (1-5). Reflects inherent
                    importance—higher scores indicate topics where gaps pose greater institutional risk.
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-500/20 text-amber-600 border border-amber-500/30">MODERATE</span>
                    <span className="font-medium">AI Exposure Assessment</span>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    <strong>Dynamic assessment</strong> based on what the AI found in this vendor's
                    documents. Levels: NEGLIGIBLE, LOW, MODERATE, HIGH, SEVERE, CRITICAL.
                  </p>
                </div>
              </div>
              <p className="text-muted-foreground text-xs mt-3">
                <strong>Example:</strong> "Data retention" might have KB score 4/5 (important topic),
                but if the vendor's policy fully addresses it, AI assessment could be NEGLIGIBLE.
              </p>
            </section>

            {/* Configuring Knowledge Base */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-500" />
                Knowledge Base
              </h2>
              <p className="text-muted-foreground mb-4">
                Each track has its own KB accessible via tabs (Privacy Guidance, Security Guidance,
                Accessibility Guidance). KB items tell the AI what to look for.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <p className="font-medium text-sm">Each KB Item Contains:</p>
                <div className="space-y-2 text-xs">
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground w-28 shrink-0">Question</span>
                    <span className="text-muted-foreground">What to look for in vendor documents</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground w-28 shrink-0">Why It Matters</span>
                    <span className="text-muted-foreground">Compliance context and risk implications</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground w-28 shrink-0">What to Look For</span>
                    <span className="text-muted-foreground">Specific criteria, red flags, best practices</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground w-28 shrink-0">Risk Score</span>
                    <span className="text-muted-foreground">1-5 weight for institutional risk</span>
                  </div>
                </div>
              </div>
              <p className="text-muted-foreground text-xs mt-3">
                Edit KB items to customize analysis. Changes apply to future analyses only.
              </p>
            </section>

            {/* Quick Reference */}
            <section className="pb-4">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-slate-500" />
                Quick Reference
              </h2>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="font-medium mb-2">Actions</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• <strong>Run All</strong> — analyze all tracks</li>
                    <li>• <strong>Run (per track)</strong> — analyze one track</li>
                    <li>• <strong>Export Report</strong> — PDF for Workday</li>
                  </ul>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="font-medium mb-2">Escalation</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• <strong>CRITICAL/SEVERE</strong> — escalate immediately</li>
                    <li>• <strong>HIGH</strong> — requires team review</li>
                    <li>• <strong>MODERATE</strong> — document and monitor</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

// Helper components

interface StepProps {
  number: number
  title: string
  description: string
}

const Step: FC<StepProps> = ({ number, title, description }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
      {number}
    </div>
    <div className="flex-1">
      <p className="font-medium">{title}</p>
      <p className="text-muted-foreground text-xs mt-1">{description}</p>
    </div>
  </div>
)

interface TrackProps {
  icon: React.ReactNode
  name: string
  team: string
  documents: string
  checks: string
}

const Track: FC<TrackProps> = ({ icon, name, team, documents, checks }) => (
  <div className="flex gap-3 items-start">
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
      {icon}
    </div>
    <div className="flex-1">
      <p className="font-medium">{name} <span className="font-normal text-muted-foreground">— {team}</span></p>
      <p className="text-xs text-muted-foreground mt-0.5">
        <span className="font-medium">Docs:</span> {documents}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">
        <span className="font-medium">Checks:</span> {checks}
      </p>
    </div>
  </div>
)
